// pages/SettingsPage.jsx
import { useState, useEffect } from "react";
import { useNotification } from "../context/NotificationContext";
import { PageHeader, Card, Toggle } from "../components/UI";
import * as api from "../services/dynamicPriceApi";

const companyId = api.getCompanyId();

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const TABS = [
  { key: "general",  label: "General",          icon: "🏨" },
  { key: "channels", label: "Channels & Rates",  icon: "📡" },
  { key: "rules",    label: "Pricing Rules",     icon: "📐" },
  { key: "seasons",  label: "Peak Seasons",      icon: "🌅" },
  { key: "events",   label: "Events",            icon: "🎯" },
  { key: "schedule", label: "Schedule",          icon: "⏰" },
];

const SRC_CLR = {
  PMS: { color: "#6d28d9", bg: "#f5f3ff", border: "#c4b5fd" },
  BE:  { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  OTA: { color: "#be185d", bg: "#fdf2f8", border: "#f9a8d4" },
};

// Season type → display colour
const SEASON_CLR = {
  peak:     { label: "Peak",     color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  shoulder: { label: "Shoulder", color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  off:      { label: "Off",      color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1" },
};

// Default empty season form
const EMPTY_SEASON = { label: "", start_date: "", end_date: "", type: "peak", markup_pct: 0 };

// Default empty event form
const EMPTY_EVENT  = { name: "", event_date: "", markup_pct: 0 };

// ─────────────────────────────────────────────────────────────
// Helper: format ISO date string → "15 Dec 2026"
// ─────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

// ─────────────────────────────────────────────────────────────
// Helper: flatten settings state → API payload shape
// ─────────────────────────────────────────────────────────────
function toApiPayload(s) {
  return {
    hotel_name:              s.hotel_name,
    city:                    s.city,
    currency:                s.currency,
    days_ahead:              s.days_ahead,
    auto_approve:            s.auto_approve,
    active_channels:         s.sources,
    commission_pms:          s.channel_commissions.PMS,
    commission_be:           s.channel_commissions.BE,
    commission_ota:          s.channel_commissions.OTA,
    occ_high_threshold:      s.markup_rules.occupancy_high.threshold,
    occ_high_markup_pct:     s.markup_rules.occupancy_high.markup_pct,
    occ_low_threshold:       s.markup_rules.occupancy_low.threshold,
    occ_low_markdown_pct:    s.markup_rules.occupancy_low.markdown_pct,
    holiday_markup_pct:      s.markup_rules.holiday.markup_pct,
    event_markup_pct:        s.markup_rules.event.markup_pct,
    last_minute_markup_pct:  s.markup_rules.days_until_3.markup_pct,
    cron_enabled:            s.cron_enabled,
    cron_time:               s.cron_time,
    // seasons & events are saved via their own endpoints (not in ai_settings)
    // included here as reference so backend can extend if needed
  };
}

// ─────────────────────────────────────────────────────────────
// Helper: map flat API response → nested UI state shape
// ─────────────────────────────────────────────────────────────
function fromApiResponse(raw) {
  return {
    hotel_name:   raw.hotel_name   ?? "",
    city:         raw.city         ?? "",
    currency:     raw.currency     ?? "INR",
    days_ahead:   raw.days_ahead   ?? 30,
    auto_approve: raw.auto_approve ?? false,
    cron_enabled: raw.cron_enabled ?? true,
    cron_time:    raw.cron_time    ?? "06:00",
    sources:      raw.active_channels ?? ["PMS", "BE", "OTA"],
    channel_commissions: {
      PMS: raw.commission_pms ?? 0,
      BE:  raw.commission_be  ?? 0,
      OTA: raw.commission_ota ?? 15,
    },
    markup_rules: {
      occupancy_high: { threshold: raw.occ_high_threshold  ?? 80, markup_pct:   raw.occ_high_markup_pct  ?? 20 },
      occupancy_low:  { threshold: raw.occ_low_threshold   ?? 40, markdown_pct: raw.occ_low_markdown_pct ?? 15 },
      holiday:        { markup_pct: raw.holiday_markup_pct    ?? 15 },
      event:          { markup_pct: raw.event_markup_pct      ?? 25 },
      days_until_3:   { markup_pct: raw.last_minute_markup_pct ?? 10 },
    },
  };
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const { notify } = useNotification();

  // ── Core settings state ───────────────────────────────────
  const [s,        setS]       = useState(null);
  const [tab,      setTab]     = useState("general");
  const [saving,   setSaving]  = useState(false);
  const [loading,  setLoading] = useState(true);

  // ── Cron history ──────────────────────────────────────────
  const [cronHistory, setCronHistory] = useState([]);

  // ── Seasons state ─────────────────────────────────────────
  const [seasons,      setSeasons]     = useState([]);
  const [seasonForm,   setSeasonForm]  = useState(EMPTY_SEASON);
  const [addingSeason, setAddingSeason]= useState(false);

  // ── Events state ──────────────────────────────────────────
  const [events,      setEvents]      = useState([]);   // manual + auto-imported combined
  const [eventForm,   setEventForm]   = useState(EMPTY_EVENT);
  const [addingEvent, setAddingEvent] = useState(false);

  // ── Load on mount ─────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getSettings(companyId),
      api.getCronHistory(companyId),
      // Events API: silently ignored if endpoint not ready yet
      (typeof api.getEvents === "function"
        ? api.getEvents(companyId).catch(() => ({ data: { data: [] } }))
        : Promise.resolve({ data: { data: [] } })),
    ])
      .then(([settingsRes, cronRes, eventsRes]) => {
        setS(fromApiResponse(settingsRes.data.data));
        setCronHistory(cronRes.data.data ?? []);
        // Merge API events (marked source:"auto") with any locally persisted manual events
        const apiEvents = (eventsRes.data.data ?? []).map(e => ({ ...e, source: "auto" }));
        setEvents(apiEvents);
      })
      .catch(() => notify("error", "Failed to load settings."))
      .finally(() => setLoading(false));
  }, []);

  // ── State updaters (unchanged from v1) ────────────────────
  const upd      = (k, v) => setS(p => ({ ...p, [k]: v }));
  const updRule  = (rule, k, v) => setS(p => ({ ...p, markup_rules: { ...p.markup_rules, [rule]: { ...p.markup_rules[rule], [k]: v } } }));
  const updComm  = (src, v) => setS(p => ({ ...p, channel_commissions: { ...p.channel_commissions, [src]: Number(v) } }));
  const toggleSrc= (src) => setS(p => ({ ...p, sources: p.sources.includes(src) ? p.sources.filter(x => x !== src) : [...p.sources, src] }));

  // ── Save settings ─────────────────────────────────────────
  const handleSave = async () => {
    if (!s) return;
    setSaving(true);
    try {
      await api.saveSettings(companyId, toApiPayload(s));

      await Promise.all([
        api.saveSettings(companyId, toApiPayload(s)),
        api.saveSeasons(companyId, seasons),
        api.saveEvents(companyId, events.filter(e => e.source === "manual")),
      ]);
      notify("success", "Settings saved successfully.");
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  // ── Season handlers ───────────────────────────────────────
  const handleAddSeason = () => {
    const { label, start_date, end_date, markup_pct } = seasonForm;
    if (!label.trim())    return notify("error", "Season label is required.");
    if (!start_date)      return notify("error", "Start date is required.");
    if (!end_date)        return notify("error", "End date is required.");
    if (end_date < start_date) return notify("error", "End date must be after start date.");

    setSeasons(prev => [...prev, { ...seasonForm, id: Date.now(), markup_pct: Number(markup_pct) }]);
    setSeasonForm(EMPTY_SEASON);
    setAddingSeason(false);
    notify("success", `"${label}" season added. Save settings to apply.`);
  };

  const handleDeleteSeason = (id) => {
    setSeasons(prev => prev.filter(s => s.id !== id));
  };

  // ── Event handlers ────────────────────────────────────────
  const handleAddEvent = () => {
    const { name, event_date, markup_pct } = eventForm;
    if (!name.trim())   return notify("error", "Event name is required.");
    if (!event_date)    return notify("error", "Event date is required.");

    setEvents(prev => [...prev, {
      ...eventForm,
      id:         Date.now(),
      source:     "manual",
      markup_pct: Number(markup_pct),
    }]);
    setEventForm(EMPTY_EVENT);
    setAddingEvent(false);
    notify("success", `"${name}" added. Save settings to apply.`);
  };

  const handleDeleteEvent = (id) => {
    // Only manual events can be deleted; auto events can only have markup edited
    setEvents(prev => prev.filter(e => e.id !== id));
  };

  const handleEditEventMarkup = (id, val) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, markup_pct: Number(val) } : e));
  };

  // ── Tab style ─────────────────────────────────────────────
  const tabStyle = (active) => ({
    display: "flex", alignItems: "center", gap: 7,
    padding: "9px 20px", border: "none", borderRadius: 9, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
    background: active ? "#2563eb" : "transparent",
    color:      active ? "#fff"    : "#64748b",
    transition: "all 0.15s",
  });

  // ── Loading ───────────────────────────────────────────────
  if (loading || !s) {
    return (
      <div style={{ padding: "28px 32px" }}>
        <div style={{ fontSize: 14, color: "#94a3b8", fontWeight: 500 }}>Loading settings…</div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "28px 32px" }}>
      <PageHeader
        title="Settings"
        subtitle="Configure pricing rules, active channels, seasons, events and automation schedule"
      >
        <button className="dp-btn dp-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="dp-spin">⟳</span> Saving…</> : "Save Settings"}
        </button>
      </PageHeader>

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 22, padding: 5, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", width: "fit-content", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabStyle(tab === t.key)}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      <Card style={{ padding: "28px 32px", marginBottom: 20 }}>

        {/* ══════════════════════════════════════════════════
            TAB: GENERAL
        ══════════════════════════════════════════════════ */}
        {tab === "general" && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 20 }}>Hotel Information</div>
            <div className="row g-3 mb-4">
              {[
                { label: "Hotel Name", key: "hotel_name", type: "text" },
                { label: "City",       key: "city",       type: "text" },
              ].map(f => (
                <div key={f.key} className="col-md-6">
                  <label className="dp-label">{f.label}</label>
                  <input type={f.type} className="dp-input" value={s[f.key]} onChange={e => upd(f.key, e.target.value)} />
                </div>
              ))}
              <div className="col-md-6">
                <label className="dp-label">Currency</label>
                <select className="dp-select" value={s.currency} onChange={e => upd("currency", e.target.value)}>
                  <option value="INR">INR — Indian Rupee</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — British Pound</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="dp-label">Forecast Days Ahead</label>
                <input type="number" className="dp-input" value={s.days_ahead} min={7} max={90}
                  onChange={e => upd("days_ahead", Number(e.target.value))} />
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 5 }}>
                  AI generates recommendations this many days in advance.
                </div>
              </div>
            </div>

            <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 5 }}>Auto-Approve</div>
              <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
                When enabled, all AI recommendations are automatically applied — no manager review needed.
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Auto-Approve AI Recommendations</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>AI prices go live immediately after each analysis run.</div>
                </div>
                <Toggle on={s.auto_approve} onToggle={() => upd("auto_approve", !s.auto_approve)} />
              </div>
              {s.auto_approve && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8 }}>
                  <span style={{ fontSize: 12, color: "#b91c1c", fontWeight: 700 }}>
                    ⚠ Auto-approve is ON. AI prices will be applied without your review. Use with caution.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: CHANNELS & RATES
        ══════════════════════════════════════════════════ */}
        {tab === "channels" && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 5 }}>Active Channels</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
              Select which booking channels the AI generates separate pricing recommendations for.
            </div>

            <div className="row g-3 mb-4">
              {[
                { key: "PMS", label: "PMS / Walk-in",  icon: "🏨", desc: "Front desk and direct walk-in rates." },
                { key: "BE",  label: "Booking Engine", icon: "🌐", desc: "Your hotel website direct booking portal." },
                { key: "OTA", label: "OTA Channels",   icon: "📱", desc: "MakeMyTrip, Booking.com, Expedia etc." },
              ].map(ch => {
                const active = s.sources.includes(ch.key);
                return (
                  <div key={ch.key} className="col-md-4">
                    <div
                      onClick={() => toggleSrc(ch.key)}
                      style={{
                        background: active ? "#eff6ff" : "#f8fafc",
                        borderRadius: 14, padding: "22px", textAlign: "center",
                        border: `2px solid ${active ? "#2563eb" : "#e2e8f0"}`,
                        cursor: "pointer", transition: "all 0.15s",
                        boxShadow: active ? "0 4px 16px rgba(37,99,235,0.12)" : "none",
                      }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 12 }}>{ch.icon}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: active ? "#2563eb" : "#0f172a", marginBottom: 8 }}>{ch.label}</div>
                      <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, marginBottom: 12 }}>{ch.desc}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: active ? "#2563eb" : "#94a3b8" }}>
                        {active ? "✓ Active" : "○ Disabled"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 5 }}>Commission Rates</div>
              <div style={{ fontSize: 13, color: "#64748b", marginBottom: 18 }}>
                AI uses these rates to calculate net revenue per channel. Individual rate plans can override these in Integration settings.
              </div>
              <div className="row g-3">
                {Object.entries(s.channel_commissions).map(([src, comm]) => {
                  const cc = SRC_CLR[src] || SRC_CLR.PMS;
                  return (
                    <div key={src} className="col-md-4">
                      <div style={{ background: "#f8fafc", borderRadius: 12, padding: "18px 20px", border: "1px solid #e2e8f0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                          <span style={{ fontSize: 11, fontWeight: 800, color: cc.color, background: cc.bg, border: `1.5px solid ${cc.border}`, borderRadius: 5, padding: "3px 9px" }}>{src}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Commission</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input type="number" value={comm} min={0} max={40}
                            onChange={e => updComm(src, e.target.value)}
                            className="dp-input"
                            style={{ width: 70, textAlign: "center", fontFamily: "'DM Mono',monospace", fontWeight: 700 }}
                          />
                          <span style={{ fontSize: 16, color: "#64748b", fontWeight: 600 }}>%</span>
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
                          {comm === 0 ? "No commission — full revenue kept" : `Hotel nets ${100 - comm}% of gross price`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: PRICING RULES
        ══════════════════════════════════════════════════ */}
        {tab === "rules" && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 5 }}>AI Pricing Rules</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 22 }}>
              These multipliers are applied on top of base rate when the AI detects specific demand signals.
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { key: "occupancy_high", label: "High Occupancy Premium",     field: "markup_pct",   icon: "📈", color: "#16a34a",
                  desc: "When occupancy exceeds threshold, add a demand premium.",
                  extra: { key: "threshold", label: "Occ Threshold %", val: s.markup_rules.occupancy_high.threshold } },
                { key: "occupancy_low",  label: "Low Occupancy Discount",     field: "markdown_pct", icon: "📉", color: "#dc2626",
                  desc: "When occupancy falls below threshold, apply markdown to drive bookings.",
                  extra: { key: "threshold", label: "Occ Threshold %", val: s.markup_rules.occupancy_low.threshold } },
                { key: "holiday",        label: "Public Holiday Premium",     field: "markup_pct",   icon: "🎉", color: "#d97706",
                  desc: "Surcharge applied automatically on detected Indian public holidays." },
                { key: "event",          label: "Local Event Surcharge",      field: "markup_pct",   icon: "🎵", color: "#8b5cf6",
                  desc: "Default surcharge when an event is detected. Individual events can override this in Seasons & Events." },
                { key: "days_until_3",   label: "Last-Minute Urgency Markup", field: "markup_pct",   icon: "⏰", color: "#2563eb",
                  desc: "Applied when check-in date is within 3 days — captures last-minute demand." },
              ].map(rule => (
                <div key={rule.key} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 18px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", borderLeft: `4px solid ${rule.color}` }}>
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{rule.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>{rule.label}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{rule.desc}</div>
                  </div>
                  {rule.extra && (
                    <div style={{ textAlign: "center", flexShrink: 0 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 5, fontWeight: 700 }}>{rule.extra.label}</div>
                      <input type="number" value={rule.extra.val}
                        onChange={e => updRule(rule.key, "threshold", Number(e.target.value))}
                        className="dp-input"
                        style={{ width: 68, textAlign: "center", fontFamily: "'DM Mono',monospace", fontWeight: 700, padding: "7px 8px" }}
                      />
                    </div>
                  )}
                  <div style={{ textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 5, fontWeight: 700 }}>
                      {rule.field === "markdown_pct" ? "Markdown %" : "Markup %"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <input type="number" value={s.markup_rules[rule.key][rule.field]}
                        onChange={e => updRule(rule.key, rule.field, Number(e.target.value))}
                        className="dp-input"
                        style={{ width: 68, textAlign: "center", fontFamily: "'DM Mono',monospace", fontWeight: 700, padding: "7px 8px" }}
                      />
                      <span style={{ fontSize: 14, color: "#64748b", fontWeight: 600 }}>%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, padding: "14px 18px", background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8", marginBottom: 5 }}>ℹ How rules combine</div>
              <div style={{ fontSize: 12, color: "#1e40af", lineHeight: 1.7 }}>
                Rules are <strong>additive</strong>. Holiday (15%) + High Occupancy (20%) + Peak Season (25%) = up to 60% above base.
                AI confidence score reflects certainty in the combined signal. You can always override any recommendation.
                Season-specific markups are configured in the <strong>Peak Seasons</strong> tab. Event-specific markups are in the <strong>Events</strong> tab.
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: PEAK SEASONS
        ══════════════════════════════════════════════════ */}
        {tab === "seasons" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Peak Seasons</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, marginBottom: 20 }}>
                  Define date ranges where AI pricing adjusts for your hotel's seasonal demand patterns.
                  Markups are additive with other rules (holiday, event etc.).
                </div>
              </div>
              {!addingSeason && (
                <button className="dp-btn dp-btn-primary dp-btn-sm" onClick={() => setAddingSeason(true)}>
                  + Add Season
                </button>
              )}
            </div>

            {/* Add season form */}
            {addingSeason && (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 22px", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>New Season</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="dp-label">Season Label *</label>
                    <input
                      type="text" className="dp-input"
                      placeholder="e.g. Wedding Season, Summer Peak"
                      value={seasonForm.label}
                      onChange={e => setSeasonForm(f => ({ ...f, label: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="dp-label">Type</label>
                    <select className="dp-select" value={seasonForm.type}
                      onChange={e => setSeasonForm(f => ({ ...f, type: e.target.value }))}>
                      <option value="peak">Peak</option>
                      <option value="shoulder">Shoulder</option>
                      <option value="off">Off</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="dp-label">Start Date *</label>
                    <input type="date" className="dp-input" value={seasonForm.start_date}
                      onChange={e => setSeasonForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div className="col-md-2">
                    <label className="dp-label">End Date *</label>
                    <input type="date" className="dp-input" value={seasonForm.end_date}
                      onChange={e => setSeasonForm(f => ({ ...f, end_date: e.target.value }))} />
                  </div>
                  <div className="col-md-2">
                    <label className="dp-label">Markup %</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="number" className="dp-input" value={seasonForm.markup_pct} min={-50} max={100}
                        style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700 }}
                        onChange={e => setSeasonForm(f => ({ ...f, markup_pct: e.target.value }))} />
                      <span style={{ color: "#64748b", fontWeight: 600 }}>%</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button className="dp-btn dp-btn-success dp-btn-sm" onClick={handleAddSeason}>Save Season</button>
                  <button className="dp-btn dp-btn-ghost dp-btn-sm" onClick={() => { setAddingSeason(false); setSeasonForm(EMPTY_SEASON); }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Seasons list */}
            {seasons.length === 0 && !addingSeason && (
              <div style={{ padding: "28px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No seasons defined yet.
                AI will use month-based defaults until you add custom date ranges.
                <br />
                <button className="dp-btn dp-btn-outline dp-btn-sm" style={{ marginTop: 12 }} onClick={() => setAddingSeason(true)}>
                  + Add your first season
                </button>
              </div>
            )}

            {seasons.map(season => {
              const clr = SEASON_CLR[season.type] || SEASON_CLR.peak;
              return (
                <div key={season.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 8, borderLeft: `4px solid ${clr.color}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{season.label}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: clr.color, background: clr.bg, border: `1px solid ${clr.border}`, borderRadius: 20, padding: "2px 8px" }}>
                        {clr.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {fmtDate(season.start_date)} → {fmtDate(season.end_date)}
                    </div>
                  </div>
                  <div style={{ textAlign: "center", minWidth: 80 }}>
                    <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3, fontWeight: 700 }}>Markup</div>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 800, fontSize: 15, color: season.markup_pct >= 0 ? "#15803d" : "#b91c1c" }}>
                      {season.markup_pct >= 0 ? "+" : ""}{season.markup_pct}%
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteSeason(season.id)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, padding: "4px 8px" }}
                    title="Remove season"
                  >✕</button>
                </div>
              );
            })}

            <div style={{ marginTop: 16, padding: "12px 16px", background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: "#1e40af", lineHeight: 1.7 }}>
                <strong>ℹ Markups are additive.</strong> A date in a Peak Season (+20%) also matching a public holiday (+15%)
                will be priced up to +35% above base. Configure event markups in the <strong>Events</strong> tab.
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: EVENTS
        ══════════════════════════════════════════════════ */}
        {tab === "events" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Local Events</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, marginBottom: 16 }}>
                  Events drive one-time price spikes. Auto-imported events come from our events API.
                  You can add your own and edit the markup on any event.
                </div>
              </div>
              {!addingEvent && (
                <button className="dp-btn dp-btn-primary dp-btn-sm" onClick={() => setAddingEvent(true)}>
                  + Add Event
                </button>
              )}
            </div>

            {/* Add event form */}
            {addingEvent && (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 22px", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>New Event</div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="dp-label">Event Name *</label>
                    <input type="text" className="dp-input"
                      placeholder="e.g. IPL Match, Chandigarh Marathon"
                      value={eventForm.name}
                      onChange={e => setEventForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="dp-label">Date *</label>
                    <input type="date" className="dp-input" value={eventForm.event_date}
                      onChange={e => setEventForm(f => ({ ...f, event_date: e.target.value }))} />
                  </div>
                  <div className="col-md-2">
                    <label className="dp-label">Markup %</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="number" className="dp-input" value={eventForm.markup_pct} min={0} max={100}
                        style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700 }}
                        onChange={e => setEventForm(f => ({ ...f, markup_pct: e.target.value }))} />
                      <span style={{ color: "#64748b", fontWeight: 600 }}>%</span>
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                  If markup is 0, the default "Local Event Surcharge" % from Pricing Rules will apply.
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <button className="dp-btn dp-btn-success dp-btn-sm" onClick={handleAddEvent}>Save Event</button>
                  <button className="dp-btn dp-btn-ghost dp-btn-sm" onClick={() => { setAddingEvent(false); setEventForm(EMPTY_EVENT); }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Events list */}
            {events.length === 0 && !addingEvent && (
              <div style={{ padding: "28px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No events yet. Auto-import will populate this list when the events API is connected.
                <br />
                <button className="dp-btn dp-btn-outline dp-btn-sm" style={{ marginTop: 12 }} onClick={() => setAddingEvent(true)}>
                  + Add manually
                </button>
              </div>
            )}

            {events
              .slice()
              .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""))
              .map(ev => (
                <div key={ev.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{ev.name}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 800, borderRadius: 20, padding: "2px 7px",
                        color:      ev.source === "auto" ? "#0369a1" : "#6d28d9",
                        background: ev.source === "auto" ? "#e0f2fe"  : "#f5f3ff",
                        border:     `1px solid ${ev.source === "auto" ? "#7dd3fc" : "#c4b5fd"}`,
                        textTransform: "uppercase", letterSpacing: 0.5,
                      }}>
                        {ev.source === "auto" ? "Auto" : "Manual"}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{fmtDate(ev.event_date)}</div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginRight: 4 }}>Markup</div>
                    <input
                      type="number" min={0} max={100}
                      value={ev.markup_pct}
                      onChange={e => handleEditEventMarkup(ev.id, e.target.value)}
                      className="dp-input"
                      style={{ width: 60, textAlign: "center", fontFamily: "'DM Mono',monospace", fontWeight: 700, padding: "5px 6px" }}
                    />
                    <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>%</span>
                  </div>

                  {ev.source === "manual" ? (
                    <button
                      onClick={() => handleDeleteEvent(ev.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, padding: "4px 8px" }}
                      title="Remove event"
                    >✕</button>
                  ) : (
                    <div style={{ width: 30 }} />
                  )}
                </div>
              ))}

            <div style={{ marginTop: 16, padding: "12px 16px", background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: "#1e40af", lineHeight: 1.7 }}>
                <strong>ℹ Markups are additive.</strong> If a date falls in a Peak Season (+20%) and has a local event (+25%),
                the AI applies up to +45% above base. Public holiday and occupancy rules also stack on top.
                Configure season markups in the <strong>Peak Seasons</strong> tab.
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: SCHEDULE
        ══════════════════════════════════════════════════ */}
        {tab === "schedule" && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 5 }}>Automated Analysis Schedule</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 22 }}>
              Configure when the AI automatically runs and generates pricing recommendations.
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Enable Daily Cron Job</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>Automatically runs AI analysis every morning and creates PENDING recommendations.</div>
              </div>
              <Toggle on={s.cron_enabled} onToggle={() => upd("cron_enabled", !s.cron_enabled)} />
            </div>

            {s.cron_enabled && (
              <>
                <div style={{ marginBottom: 22, maxWidth: 280 }}>
                  <label className="dp-label">Run Time (24-hour)</label>
                  <input type="time" value={s.cron_time} onChange={e => upd("cron_time", e.target.value)} className="dp-input" />
                </div>

                <div style={{ marginBottom: 22, padding: "16px 18px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#166534", marginBottom: 10 }}>✓ Schedule Active</div>
                  {[
                    `Runs every day at ${s.cron_time}`,
                    `Generates recommendations for the next ${s.days_ahead} days`,
                    `Covers ${(s.sources || []).join(", ")} channels`,
                    seasons.length > 0 ? `${seasons.length} custom season${seasons.length > 1 ? "s" : ""} active` : "Using default month-based seasons",
                    events.length > 0 ? `${events.length} event${events.length > 1 ? "s" : ""} loaded` : "No events loaded",
                    s.auto_approve ? "Auto-approves all recommendations" : "Creates recommendations as PENDING — awaiting review",
                  ].map(item => (
                    <div key={item} style={{ fontSize: 13, color: "#166534", marginBottom: 4 }}>● {item}</div>
                  ))}
                </div>

                {/* Cron run history — live from API */}
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Run History</div>
                {cronHistory.length === 0 && (
                  <div style={{ fontSize: 13, color: "#94a3b8", padding: "12px 0" }}>No runs recorded yet.</div>
                )}
                {cronHistory.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    {cronHistory.map((run, i) => {
                      const ok       = run.status === "success";
                      const timeStr  = run.started_at
                        ? new Date(run.started_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })
                        : "—";
                      const duration = run.duration_seconds ? `${Math.round(run.duration_seconds)}s` : "—";
                      return (
                        <div key={run.id ?? i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderBottom: i < cronHistory.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                          <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>{timeStr}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                            {ok && <span style={{ fontSize: 12, color: "#64748b" }}>{run.recs_generated ?? 0} recs generated</span>}
                            {ok && <span style={{ fontSize: 12, color: "#64748b" }}>{duration}</span>}
                            {!ok && run.error_message && (
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>{run.error_message}</span>
                            )}
                            <span style={{ fontSize: 12, fontWeight: 700, color: ok ? "#15803d" : "#b91c1c" }}>
                              {ok ? "✓ Success" : "✕ Failed"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {!s.cron_enabled && (
              <div style={{ padding: "14px 18px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: "#92400e" }}>
                  ⚠ Cron job is disabled. You must manually click <strong>Run AI Analysis</strong> from the Dashboard to generate recommendations.
                </div>
              </div>
            )}
          </div>
        )}

      </Card>
    </div>
  );
}
